import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "../hooks/useAuth.js";
import { friendlyMessage } from "@/shared/lib/errors.js";
import { Logo } from "@/shared/ui/logo.js";
import { Button } from "@/shared/ui/button.js";
import { Input } from "@/shared/ui/input.js";
import { cn } from "@/shared/lib/utils.js";

const labelClass = "font-mono text-[0.6875rem] font-medium uppercase tracking-[0.15em] text-muted-foreground";
const fieldClass =
  "h-11 bg-background/60 pl-10 transition-[border-color,box-shadow] focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:border-ring";

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(friendlyMessage(err, "login"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="paper-surface relative grid min-h-screen place-items-center overflow-hidden px-4 py-10">
      {/* Barra de acento ámbar (firma FORJA) */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-1 bg-brand-accent" />
      {/* Grilla técnica de fondo */}
      <div aria-hidden className="paper-grid pointer-events-none absolute inset-0 opacity-60" />

      <div className="relative w-full max-w-md">
        {/* Cabecera editorial */}
        <div className="forja-rise mb-8 text-center" style={{ animationDelay: "60ms" }}>
          <div className="mb-5 flex justify-center">
            <Logo height={40} />
          </div>
          <p className={cn(labelClass, "mb-3")}>Reportes Técnicos de Campo</p>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">
            Iniciar <span className="italic font-medium">sesión</span>
          </h1>
        </div>

        {/* Tarjeta del formulario */}
        <div
          className="forja-rise rounded-lg border border-border bg-card p-6 shadow-[0_1px_0_0_var(--border),0_12px_40px_-12px_rgba(26,26,24,0.18)] sm:p-8"
          style={{ animationDelay: "140ms" }}
        >
          <form onSubmit={onSubmit} className="grid gap-5">
            <div className="grid gap-2">
              <label htmlFor="email" className={labelClass}>
                Correo electrónico
              </label>
              <div className="relative">
                <Mail
                  aria-hidden
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="admin@marco.com.pe"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={fieldClass}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <label htmlFor="password" className={labelClass}>
                Contraseña
              </label>
              <div className="relative">
                <Lock
                  aria-hidden
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={fieldClass}
                />
              </div>
            </div>

            {error && (
              <p
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm font-medium text-destructive"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="mt-1 w-full gap-2 font-mono text-sm font-semibold uppercase tracking-[0.12em]"
            >
              {loading ? "Ingresando…" : "Entrar"}
              {!loading && <ArrowRight className="size-4" aria-hidden />}
            </Button>
          </form>
        </div>

        <p
          className="forja-rise mt-6 text-center font-mono text-[0.6875rem] uppercase tracking-[0.15em] text-muted-foreground/70"
          style={{ animationDelay: "220ms" }}
        >
          MARCO · Soluciones Integrales
        </p>
      </div>
    </div>
  );
}
